import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel"]

  connect() {
    document.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  handleKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.open()
    }
    if (event.key === "Escape") {
      this.close()
    }
  }

  open() {
    const panel = document.getElementById("quick-info-panel")
    if (panel) {
      panel.classList.remove("hidden")
      this.renderHelpContent()
    }
  }

  close() {
    const panel = document.getElementById("quick-info-panel")
    if (panel) {
      panel.classList.add("hidden")
    }
  }

  renderHelpContent() {
    const panel = document.getElementById("quick-info-panel")
    const contentArea = panel?.querySelector("[data-quick-info-target='panel']")
    if (!contentArea) return

    contentArea.innerHTML = this.getHelpHTML()
    this.showTab("dashboard")

    // Focus search input
    const searchInput = contentArea.querySelector("#quick-search-input")
    if (searchInput) {
      setTimeout(() => searchInput.focus(), 100)
      searchInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && searchInput.value.trim()) {
          window.location.href = `/sm/search?query=${encodeURIComponent(searchInput.value.trim())}`
        }
      })
    }
  }

  showTab(tabId) {
    // Hide all tab contents
    const contents = document.querySelectorAll("[data-help-tab-content]")
    contents.forEach(c => c.classList.add("hidden"))

    // Show selected tab content
    const selectedContent = document.getElementById(`help-${tabId}`)
    if (selectedContent) selectedContent.classList.remove("hidden")

    // Update tab buttons
    const buttons = document.querySelectorAll("[data-help-tab]")
    buttons.forEach(btn => {
      const isSelected = btn.dataset.helpTab === tabId
      btn.className = isSelected
        ? "flex items-center px-3 py-2 text-sm font-medium text-[#C4A484] bg-[#C4A484]/10 rounded-lg"
        : "flex items-center px-3 py-2 text-sm text-[#8C8C8C] hover:bg-[#F5F3F0] rounded-lg transition-colors"
    })
  }

  getHelpHTML() {
    return `
      <div class="h-full flex flex-col">
        <!-- Search Bar -->
        <div class="p-4 border-b border-[#E8E4DF]">
          <div class="relative">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input id="quick-search-input" type="text" placeholder="Search clients, tasks, notes..." class="w-full pl-10 pr-4 py-2.5 border border-[#E8E4DF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C4A484] focus:border-transparent bg-[#F5F3F0]">
            <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8C8C8C] bg-[#E8E4DF] px-1.5 py-0.5 rounded">Enter</span>
          </div>
        </div>

        <!-- Header -->
        <div class="p-6 border-b border-[#E8E4DF]">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-lg font-semibold text-[#2D2D2D]">Help & Documentation</h2>
              <p class="text-sm text-[#8C8C8C] mt-1">Learn how to use SMT Mission Control</p>
            </div>
            <button onclick="document.getElementById('quick-info-panel')?.classList.add('hidden')" class="text-[#8C8C8C] hover:text-[#2D2D2D] transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="flex flex-1 overflow-hidden">
          <!-- Tab Navigation -->
          <div class="w-44 border-r border-[#E8E4DF] p-3 overflow-y-auto">
            <nav class="space-y-1">
              ${this.getTabButtons()}
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="flex-1 overflow-y-auto p-6">
            ${this.getDashboardHelp()}
            ${this.getInboxHelp()}
            ${this.getClientsHelp()}
            ${this.getCalendarHelp()}
            ${this.getTasksHelp()}
            ${this.getInternsHelp()}
            ${this.getEngagementHelp()}
            ${this.getVerificationsHelp()}
            ${this.getAnalyticsHelp()}
            ${this.getProfileHelp()}
            ${this.getShortcutsHelp()}
          </div>
        </div>
      </div>
    `
  }

  getTabButtons() {
    const tabs = [
      { id: "dashboard", icon: this.iconGrid(), label: "Dashboard" },
      { id: "inbox", icon: this.iconInbox(), label: "Inbox" },
      { id: "clients", icon: this.iconClients(), label: "Clients" },
      { id: "calendar", icon: this.iconCalendar(), label: "Calendar" },
      { id: "tasks", icon: this.iconTasks(), label: "Tasks" },
      { id: "interns", icon: this.iconInterns(), label: "Interns" },
      { id: "engagement", icon: this.iconEngagement(), label: "Engagement" },
      { id: "verifications", icon: this.iconVerifications(), label: "Verifications" },
      { id: "analytics", icon: this.iconAnalytics(), label: "Analytics" },
      { id: "profile", icon: this.iconProfile(), label: "Profile" },
      { id: "shortcuts", icon: this.iconShortcuts(), label: "Shortcuts" },
    ]

    return tabs.map((tab, index) => `
      <button data-help-tab="${tab.id}" onclick="window.showHelpTab('${tab.id}')" class="${index === 0 ? 'flex items-center px-3 py-2 text-sm font-medium text-[#C4A484] bg-[#C4A484]/10 rounded-lg' : 'flex items-center px-3 py-2 text-sm text-[#8C8C8C] hover:bg-[#F5F3F0] rounded-lg transition-colors'}">
        ${tab.icon}
        <span class="ml-2">${tab.label}</span>
      </button>
    `).join("")
  }

  getDashboardHelp() {
    return `
      <div id="help-dashboard" data-help-tab-content class="space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Dashboard</h3>
        <p class="text-sm text-[#5A5A5A]">Your central hub for managing all client activities and staying on top of tasks.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Day View</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Shows all scheduled activities for the current day, including content posts, engagement slots, and tasks. Navigate between dates using the date picker.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Client View</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Displays a quick overview of all your assigned clients with their latest activity and pending tasks. Click any client card to view their full profile.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Quick Stats</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">At-a-glance metrics showing pending tasks, upcoming content, and engagement activities due today.</p>
          </div>
        </div>

        <div class="p-3 border border-[#C4A484] rounded-lg bg-[#C4A484]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Pro Tip:</strong> Use the keyboard shortcut <kbd class="px-1.5 py-0.5 bg-[#E8E4DF] rounded text-xs">Cmd/Ctrl + K</kbd> to quickly open this help panel from anywhere.</p>
        </div>
      </div>
    `
  }

  getInboxHelp() {
    return `
      <div id="help-inbox" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Inbox</h3>
        <p class="text-sm text-[#5A5A5A]">Your notification center for all updates and communications.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Notifications</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">All system notifications appear here, including task assignments, content approvals, verification requests, and team messages.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Unread Badge</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">The badge in the sidebar shows your unread notification count. Click a notification to mark it as read and navigate to the relevant item.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Notification Types</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Task assignments and completions</li>
              <li>Content approval requests</li>
              <li>Verification submissions</li>
              <li>Client portal messages</li>
              <li>System alerts and reminders</li>
            </ul>
          </div>
        </div>
      </div>
    `
  }

  getClientsHelp() {
    return `
      <div id="help-clients" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Clients</h3>
        <p class="text-sm text-[#5A5A5A]">Manage all your social media clients from one central location.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Client List</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">View all clients assigned to you. Use the search and filter options to quickly find specific clients. Click any client to view their full profile.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Adding a New Client</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Click "Add Client" to create a new client profile. Required fields include name and at least one social media handle. After adding, the client can be assigned to team members.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Client Profile Tabs</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Overview:</strong> Basic info, social handles, and quick stats</li>
              <li><strong>Content:</strong> Scheduled and published content items</li>
              <li><strong>Tasks:</strong> Client-specific tasks and to-dos</li>
              <li><strong>Notes:</strong> Internal notes and documentation</li>
              <li><strong>Messages:</strong> Communication with client via portal</li>
              <li><strong>Files:</strong> Uploaded documents and media</li>
              <li><strong>Settings:</strong> Client preferences and engagement slots</li>
            </ul>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Client Portal</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Each client has access to their own portal where they can view scheduled content, send messages, and submit content for review. Portal access is managed in client settings.</p>
          </div>
        </div>

        <div class="p-3 border border-[#C4A484] rounded-lg bg-[#C4A484]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Pro Tip:</strong> Create tasks directly from a client's profile page to keep work organized by client.</p>
        </div>
      </div>
    `
  }

  getCalendarHelp() {
    return `
      <div id="help-calendar" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Calendar</h3>
        <p class="text-sm text-[#5A5A5A]">Visualize and manage content schedules across all clients.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Calendar Views</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Day View:</strong> Detailed hour-by-hour schedule</li>
              <li><strong>Week View:</strong> 7-day overview of all scheduled items</li>
              <li><strong>Month View:</strong> Full month at a glance</li>
            </ul>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Content Scheduling</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Schedule content items by clicking on a date or using the "+ Add Content" button. Set the platform, posting time, and attach media files.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Color Coding</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><span class="inline-block w-2 h-2 rounded-full bg-[#E1306C]"></span> Instagram content</li>
              <li><span class="inline-block w-2 h-2 rounded-full bg-[#000000]"></span> TikTok content</li>
              <li><span class="inline-block w-2 h-2 rounded-full bg-[#1DA1F2]"></span> Twitter/X content</li>
              <li><span class="inline-block w-2 h-2 rounded-full bg-[#0077B5]"></span> LinkedIn content</li>
            </ul>
          </div>
        </div>
      </div>
    `
  }

  getTasksHelp() {
    return `
      <div id="help-tasks" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Tasks</h3>
        <p class="text-sm text-[#5A5A5A]">Track and manage all your to-dos and assignments.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Task Views</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>All:</strong> Complete list of all your tasks</li>
              <li><strong>Today:</strong> Tasks due or scheduled for today</li>
              <li><strong>Overdue:</strong> Tasks past their due date</li>
            </ul>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Creating Tasks</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Tasks are created from individual client pages to keep them organized. Navigate to a client's profile and click "New Task" to create a task associated with that client.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Task Types</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Content Creation:</strong> Creating posts, graphics, videos</li>
              <li><strong>Engagement:</strong> Community management tasks</li>
              <li><strong>Research:</strong> Competitor or trend research</li>
              <li><strong>Client Request:</strong> Tasks from client requests</li>
              <li><strong>Verification:</strong> Content approval tasks</li>
              <li><strong>General:</strong> Other miscellaneous tasks</li>
            </ul>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Priority Levels</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1">
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#E57373]/20 text-[#E57373]">Urgent</span> - Needs immediate attention</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#FFB74D]/20 text-[#FFB74D]">High</span> - Important, do soon</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#8BA4B4]/20 text-[#8BA4B4]">Medium</span> - Normal priority</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#E8E4DF] text-[#8C8C8C]">Low</span> - When time permits</li>
            </ul>
          </div>
        </div>

        <div class="p-3 border border-[#C4A484] rounded-lg bg-[#C4A484]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Pro Tip:</strong> Click the checkbox icon next to a task to quickly mark it as complete.</p>
        </div>
      </div>
    `
  }

  getInternsHelp() {
    return `
      <div id="help-interns" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Interns</h3>
        <p class="text-sm text-[#5A5A5A]">Manage intern assignments and monitor their workload.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Intern Dashboard</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">View all active interns, their assigned clients, and current task load. Click on an intern to see their detailed work history and performance.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Assigning Work</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Assign interns to specific clients or individual tasks. Interns will receive notifications when new work is assigned.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Intern vs Full Team Access</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Interns have limited access compared to full team members. They can only see clients and tasks assigned to them, and their work requires verification before publishing.</p>
          </div>
        </div>

        <div class="p-3 border border-[#8BA4B4] rounded-lg bg-[#8BA4B4]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Manager Only:</strong> This section is only visible to managers and admins.</p>
        </div>
      </div>
    `
  }

  getEngagementHelp() {
    return `
      <div id="help-engagement" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Engagement Calendar</h3>
        <p class="text-sm text-[#5A5A5A]">Schedule and track community engagement activities for clients.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Engagement Slots</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Each client can have recurring engagement slots configured in their settings. These represent times when team members should be actively engaging with their audience.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Setting Up Slots</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Configure engagement slots in a client's Settings tab. Set the platform (Instagram, TikTok, etc.), days of the week, and time ranges for engagement.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Engagement Activities</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Responding to comments</li>
              <li>Engaging with followers' content</li>
              <li>DM responses and outreach</li>
              <li>Story interactions</li>
              <li>Hashtag engagement</li>
            </ul>
          </div>
        </div>

        <div class="p-3 border border-[#8BA4B4] rounded-lg bg-[#8BA4B4]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Manager Only:</strong> This section is only visible to managers and admins.</p>
        </div>
      </div>
    `
  }

  getVerificationsHelp() {
    return `
      <div id="help-verifications" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Verifications</h3>
        <p class="text-sm text-[#5A5A5A]">Review and approve content before it goes live.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Verification Queue</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Content submitted by interns or flagged for review appears here. Review the content, captions, and scheduled time before approving.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Approval Workflow</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Review content media and caption</li>
              <li>Check posting time and platform</li>
              <li>Approve to schedule for posting</li>
              <li>Request changes if edits needed</li>
              <li>Reject if content is not suitable</li>
            </ul>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Verification Status</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1">
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#FFB74D]/20 text-[#FFB74D]">Pending</span> - Awaiting review</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#9CAF88]/20 text-[#9CAF88]">Approved</span> - Ready to publish</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#8BA4B4]/20 text-[#8BA4B4]">Changes Requested</span> - Needs edits</li>
              <li><span class="inline-block px-1.5 py-0.5 text-xs rounded-full bg-[#E57373]/20 text-[#E57373]">Rejected</span> - Not approved</li>
            </ul>
          </div>
        </div>

        <div class="p-3 border border-[#8BA4B4] rounded-lg bg-[#8BA4B4]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Manager Only:</strong> This section is only visible to managers and admins.</p>
        </div>
      </div>
    `
  }

  getAnalyticsHelp() {
    return `
      <div id="help-analytics" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Analytics</h3>
        <p class="text-sm text-[#5A5A5A]">Track performance metrics and generate reports.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Overview Dashboard</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">High-level metrics across all clients including total content posted, engagement rates, and growth trends.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Available Metrics</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Content posting frequency</li>
              <li>Engagement rates by platform</li>
              <li>Follower growth over time</li>
              <li>Task completion rates</li>
              <li>Team productivity metrics</li>
            </ul>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Client Reports</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Generate detailed reports for individual clients showing their performance metrics, content history, and growth analytics.</p>
          </div>
        </div>

        <div class="p-3 border border-[#8BA4B4] rounded-lg bg-[#8BA4B4]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Manager Only:</strong> This section is only visible to managers and admins.</p>
        </div>
      </div>
    `
  }

  getProfileHelp() {
    return `
      <div id="help-profile" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Your Profile</h3>
        <p class="text-sm text-[#5A5A5A]">Manage your account settings and preferences.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Profile Information</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Update your name, email, phone number, and profile photo. Click "Edit Profile" to make changes.</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Password Security</h4>
            <p class="text-xs text-[#8C8C8C] mt-1">Change your password in the Edit Profile page. Passwords must be at least 8 characters with one uppercase letter, one number, and one symbol (!@#$%^&*).</p>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Account Roles</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li><strong>Admin:</strong> Full system access and user management</li>
              <li><strong>Manager:</strong> Client management and team oversight</li>
              <li><strong>Team Member:</strong> Standard client work access</li>
              <li><strong>Intern:</strong> Limited access, work requires verification</li>
            </ul>
          </div>
        </div>
      </div>
    `
  }

  getShortcutsHelp() {
    return `
      <div id="help-shortcuts" data-help-tab-content class="hidden space-y-4">
        <h3 class="text-lg font-semibold text-[#2D2D2D]">Keyboard Shortcuts</h3>
        <p class="text-sm text-[#5A5A5A]">Navigate faster with these keyboard shortcuts.</p>

        <div class="space-y-3">
          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Global Shortcuts</h4>
            <table class="w-full text-xs mt-2">
              <tbody class="divide-y divide-[#E8E4DF]">
                <tr>
                  <td class="py-1.5 text-[#8C8C8C]"><kbd class="px-1.5 py-0.5 bg-white rounded border border-[#E8E4DF]">Cmd/Ctrl</kbd> + <kbd class="px-1.5 py-0.5 bg-white rounded border border-[#E8E4DF]">K</kbd></td>
                  <td class="py-1.5 text-[#2D2D2D]">Open this help panel</td>
                </tr>
                <tr>
                  <td class="py-1.5 text-[#8C8C8C]"><kbd class="px-1.5 py-0.5 bg-white rounded border border-[#E8E4DF]">Esc</kbd></td>
                  <td class="py-1.5 text-[#2D2D2D]">Close panels and modals</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="p-3 bg-[#F5F3F0] rounded-lg">
            <h4 class="font-medium text-[#2D2D2D] text-sm">Navigation Tips</h4>
            <ul class="text-xs text-[#8C8C8C] mt-1 space-y-1 list-disc list-inside">
              <li>Click the bell icon to view notifications</li>
              <li>Click your avatar to access your profile</li>
              <li>Use the sidebar to navigate between sections</li>
              <li>Use browser back/forward for page navigation</li>
            </ul>
          </div>
        </div>

        <div class="p-3 border border-[#C4A484] rounded-lg bg-[#C4A484]/5">
          <p class="text-xs text-[#5A5A5A]"><strong>Need Help?</strong> Contact your team lead or email support@trendpr.com for assistance.</p>
        </div>
      </div>
    `
  }

  // Icon helpers
  iconGrid() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>`
  }
  iconInbox() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>`
  }
  iconClients() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`
  }
  iconCalendar() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>`
  }
  iconTasks() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>`
  }
  iconInterns() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>`
  }
  iconEngagement() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>`
  }
  iconVerifications() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`
  }
  iconAnalytics() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>`
  }
  iconProfile() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>`
  }
  iconShortcuts() {
    return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>`
  }
}

// Global function for tab switching (called from dynamically generated HTML)
window.showHelpTab = function(tabId) {
  // Hide all tab contents
  const contents = document.querySelectorAll("[data-help-tab-content]")
  contents.forEach(c => c.classList.add("hidden"))

  // Show selected tab content
  const selectedContent = document.getElementById(`help-${tabId}`)
  if (selectedContent) selectedContent.classList.remove("hidden")

  // Update tab buttons
  const buttons = document.querySelectorAll("[data-help-tab]")
  buttons.forEach(btn => {
    const isSelected = btn.dataset.helpTab === tabId
    btn.className = isSelected
      ? "flex items-center px-3 py-2 text-sm font-medium text-[#C4A484] bg-[#C4A484]/10 rounded-lg"
      : "flex items-center px-3 py-2 text-sm text-[#8C8C8C] hover:bg-[#F5F3F0] rounded-lg transition-colors"
  })
}
